class MenuProductSwitcher extends HTMLElement {
  connectedCallback() {
    this.categoryLinks = Array.from(this.querySelectorAll("[data-menu-target]"));
    this.productPanels = Array.from(this.querySelectorAll("[data-menu-products-panel]"));
    this.titleElement = this.querySelector("[data-menu-products-title]");
    this.ctaElement = this.querySelector("[data-menu-products-link]");
    this.hoverQuery = window.matchMedia("(hover: hover) and (pointer: fine)");

    this.categoryLinks.forEach((link) => {
      if (this.hoverQuery.matches) {
        link.addEventListener("pointerenter", () => this.showProductsFor(link));
      }

      link.addEventListener("focusin", () => this.showProductsFor(link));

    });

    if (this.hoverQuery.matches) {
      this.addEventListener("pointerleave", () => this.showDefaultProducts());
    }
  }

  showProductsFor(link) {
    const target = link.dataset.menuTarget;
    const panel = this.productPanels.find((item) => item.dataset.menuProductsPanel === target);

    if (!panel) {
      this.showDefaultProducts();
      return;
    }

    this.productPanels.forEach((item) => {
      const isActive = item === panel;
      item.hidden = !isActive;
      item.classList.toggle("is-active", isActive);
    });

    this.categoryLinks.forEach((item) => {
      const isActive = item === link;
      item.classList.toggle("is-active", isActive);

    });

    const title = link.dataset.menuTitle || link.textContent.trim();
    const url = link.dataset.menuUrl || this.dataset.defaultUrl;

    if (this.titleElement) this.titleElement.textContent = title;
    if (this.ctaElement) {
      this.ctaElement.href = url;
      this.ctaElement.textContent = link.dataset.menuCta || title;
    }
  }

  showDefaultProducts() {
    const defaultPanel = this.productPanels.find((item) => item.dataset.menuProductsPanel === "default");

    this.productPanels.forEach((item) => {
      const isActive = item === defaultPanel;
      item.hidden = !isActive;
      item.classList.toggle("is-active", isActive);
    });

    this.categoryLinks.forEach((item) => {
      item.classList.remove("is-active");

    });

    if (this.titleElement) {
      this.titleElement.textContent = this.dataset.defaultTitle || "";
    }

    if (this.ctaElement) {
      this.ctaElement.href = this.dataset.defaultUrl || "#";
      this.ctaElement.textContent = this.dataset.defaultCta || "";
    }
  }
}

if (!window.customElements.get("menu-product-switcher")) {
  window.customElements.define("menu-product-switcher", MenuProductSwitcher);
}

const createInstantMenuAnimationControls = () => ({
  finish() {},
  finished: {
    then(callback) {
      const result = callback(true);
      return Promise.resolve(result);
    }
  }
});

const enableInstantShopAllMenu = (root = document) => {
  root.querySelectorAll("mega-menu-disclosure").forEach((disclosure) => {
    if (!disclosure.querySelector(".header-shop-all-menu")) return;

    Object.defineProperties(disclosure, {
      createShowAnimationControls: {
        configurable: true,
        value: createInstantMenuAnimationControls
      },
      createHideAnimationControls: {
        configurable: true,
        value: createInstantMenuAnimationControls
      }
    });
  });
};

const initializeInstantShopAllMenu = () => {
  window.customElements.whenDefined("mega-menu-disclosure").then(() => {
    enableInstantShopAllMenu();
  });
};

const mobileHeaderQuery = window.matchMedia("(max-width: 999px)");

const setMobileSidebarOffset = (sidebar) => {
  if (!mobileHeaderQuery.matches) return;

  const header = document.querySelector(".shopify-section--header .header");
  const headerRect = header?.getBoundingClientRect();
  const configuredHeaderHeight = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--header-height")
  );
  const headerHeight = Number.isFinite(configuredHeaderHeight)
    ? configuredHeaderHeight
    : headerRect?.height;
  const headerBottom = headerRect && Number.isFinite(headerHeight) ? headerRect.top + headerHeight : undefined;

  if (Number.isFinite(headerBottom)) {
    sidebar.style.setProperty("--header-sidebar-offset", `${Math.max(0, headerBottom).toFixed(2)}px`);
  }
};

const revealMobileSidebarItems = (sidebar) => {
  const items = [
    sidebar.querySelector(".header-sidebar__main-panel"),
    ...sidebar.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist li"),
    sidebar.querySelector(".header-mobile-menu__footer")
  ].filter(Boolean);

  items.forEach((item) => {
    item.getAnimations().forEach((animation) => animation.cancel());
    item.style.opacity = "1";
    item.style.transform = "none";
  });

  return createInstantMenuAnimationControls();
};

const enableSynchronizedMobileSidebar = (root = document) => {
  root.querySelectorAll("header-sidebar").forEach((sidebar) => {
    if (sidebar.dataset.mobileMenuSynchronized === "true") return;

    const originalRevealItems = sidebar.revealItems.bind(sidebar);

    Object.defineProperty(sidebar, "revealItems", {
      configurable: true,
      value(withDelay = false) {
        if (!mobileHeaderQuery.matches) return originalRevealItems(withDelay);
        return revealMobileSidebarItems(sidebar);
      }
    });

    sidebar.addEventListener("dialog:before-show", () => {
      if (!mobileHeaderQuery.matches) return;
      setMobileSidebarOffset(sidebar);
      revealMobileSidebarItems(sidebar);
    });

    sidebar.dataset.mobileMenuSynchronized = "true";
  });
};

const initializeSynchronizedMobileSidebar = () => {
  window.customElements.whenDefined("header-sidebar").then(() => {
    enableSynchronizedMobileSidebar();
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initializeInstantShopAllMenu();
    initializeSynchronizedMobileSidebar();
  }, { once: true });
} else {
  initializeInstantShopAllMenu();
  initializeSynchronizedMobileSidebar();
}

document.addEventListener("shopify:section:load", (event) => {
  enableInstantShopAllMenu(event.target);
  enableSynchronizedMobileSidebar(event.target);
});

const desktopMenu = document.querySelector("#desktop-menu");
const desktopMenuButton = document.querySelector('[aria-controls="desktop-menu"]');
const desktopMenuOverlay = document.querySelector(".desktop-menu-overlay");
const desktopHoverQuery = window.matchMedia("(min-width: 1000px) and (pointer: fine)");

if (desktopMenu && desktopMenuButton && desktopMenuOverlay) {
  let closeTimer;
  let openTimer;

  const syncDesktopMenuState = () => {
    desktopMenuButton.setAttribute("aria-expanded", desktopMenu.classList.contains("is-visible") ? "true" : "false");
  };

  const openDesktopMenu = () => {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    desktopMenu.classList.add("is-visible");
    desktopMenuOverlay.classList.add("is-visible");
    desktopMenuButton.setAttribute("aria-expanded", "true");
  };

  const closeDesktopMenu = () => {
    window.clearTimeout(openTimer);
    desktopMenu.classList.remove("is-visible");
    desktopMenuOverlay.classList.remove("is-visible");
    desktopMenuButton.setAttribute("aria-expanded", "false");
  };

  const scheduleDesktopMenuClose = () => {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (!desktopMenu.matches(":hover") && !desktopMenuButton.matches(":hover")) {
        closeDesktopMenu();
      }
    }, 120);
  };

  desktopMenuButton.setAttribute("aria-expanded", "false");
  desktopMenuButton.addEventListener("click", (event) => {
    event.stopImmediatePropagation();
    window.clearTimeout(openTimer);

    if (desktopMenu.classList.contains("is-visible")) {
      closeDesktopMenu();
    } else {
      openDesktopMenu();
    }
  }, true);
  desktopMenuButton.addEventListener("pointerenter", () => {
    if (!desktopHoverQuery.matches) return;

    window.clearTimeout(openTimer);
    openTimer = window.setTimeout(openDesktopMenu, 80);
  });
  desktopMenuButton.addEventListener("pointerleave", scheduleDesktopMenuClose);

  desktopMenu.addEventListener("pointerenter", () => window.clearTimeout(closeTimer));
  desktopMenu.addEventListener("pointerleave", scheduleDesktopMenuClose);
  desktopMenu.addEventListener("focusin", () => window.clearTimeout(closeTimer));
  desktopMenu.addEventListener("focusout", scheduleDesktopMenuClose);

  desktopMenuOverlay.addEventListener("click", () => window.requestAnimationFrame(syncDesktopMenuState));

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && desktopMenu.classList.contains("is-visible")) {
      closeDesktopMenu();
      desktopMenuButton.focus();
    }
  });
}
